import React, {useCallback, useEffect, useState} from 'react';
import {useGetPredictions} from 'components/customer/Settings/helpers/useGetPredictions';
import {PredictionInterface} from 'components/customer/Settings/store/settingsApi';
import styled from 'styled-components';
import {Icon} from 'shared/helpers';
import {DefaultDeliveryAddress} from 'components/customer/Settings/address/DefaultDeliveryAddress';
import {useDebouncedCallback} from 'use-debounce';
import {useGetPredictionDetails} from 'components/customer/Settings/helpers/useGetPredictionDetails';
import {useAppContext} from 'contexts';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useFormikContext} from 'formik';
import {JobForm} from 'components/customer/Job/entity/JobForm';

export const Search = ({
    isShowAddress = false,
    isDisplayOnly = false,
    isNameVisible = true,
}: {
    isShowAddress?: boolean;
    isDisplayOnly?: boolean;
    isNameVisible?: boolean;
}) => {
    const {userProfile, places} = useAppContext();
    const {getPredictions, isLoading: predicting} = useGetPredictions();
    const {getPredictionDetails} = useGetPredictionDetails();
    const isSmallDevice = isDeviceSmall();

    const [loader, setLoader] = useState(false);
    const [predictions, setPredictions] = useState<PredictionInterface[]>([]);
    const [noResults, setNoResults] = useState(false);
    const [input, setInput] = useState('');
    const [showAddress, setShowAddress] = useState(isShowAddress);
    const [displayOnly, setDisplayOnly] = useState(isDisplayOnly);
    const [states, setStates] = useState<{id: number; name: string}[]>([]);

    const {values, setFieldValue} = useFormikContext<JobForm>();

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            setInput(inputValue);
            void fetchPredictions(inputValue);
        },
        []
    );

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLLIElement>) => {
            const inputValue = event.currentTarget.innerText;
            fetchPredictions.cancel();
            setInput(inputValue);
            setShowAddress(true);
            setDisplayOnly(true);
            setPredictions([]);
            setNoResults(false);
            setLoader(true);

            getPredictionDetails(event.currentTarget.id)
                .then((data) => {
                    if (data) {
                        const currentState = states.find(
                            (state) => state.name == data.state
                        );

                        if (currentState) {
                            if (isNameVisible) {
                                void setFieldValue(
                                    'name',
                                    values && values.name ? values.name : ''
                                );
                            }

                            // only some countries have a city (e.g. NZ),
                            // api gives null for the rest
                            void setFieldValue('city', data.city ?? '');
                            void setFieldValue(
                                'street',
                                [data.streetNumber, data.streetName]
                                    .filter(Boolean)
                                    .join(' ')
                            );
                            void setFieldValue('suburb', data.suburb);
                            void setFieldValue('postcode', data.postcode);
                            void setFieldValue('state', currentState.id);
                        }
                    }
                })
                .finally(() => {
                    setLoader(false);
                });
        },
        [input, states, values, isNameVisible]
    );

    const handleNotFound = useCallback(() => {
        fetchPredictions.cancel();
        setInput('');
        setPredictions([]);
        setNoResults(false);
        setShowAddress(true);
        setDisplayOnly(false);
    }, [input]);

    const fetchPredictions = useDebouncedCallback(async (input: string) => {
        const response = await getPredictions(input);
        setPredictions(response);
        setNoResults(response.length == 0);
    }, 300);

    useEffect(() => {
        if (
            places &&
            places.Countries &&
            userProfile &&
            userProfile.countryName
        ) {
            setStates(places.Countries[userProfile.countryName]);
        }
    }, [userProfile, places]);

    return (
        <div style={{position: 'relative'}}>
            {predictions.length > 0 ? (
                <Styledul>
                    {predictions.map((prediction) => (
                        <Styledli
                            key={prediction.id}
                            id={prediction.id}
                            onClick={handleClick}>
                            <Icon
                                iconName="Location-Pin-Drop.svg"
                                style={{
                                    width: '25px',
                                    height: '25px',
                                    marginRight: '7px',
                                    cursor: 'pointer',
                                }}
                            />
                            {prediction.name}
                        </Styledli>
                    ))}
                    <StyledNotFoundli onClick={handleNotFound}>
                        Can&apos;t find the address? Add it manually
                    </StyledNotFoundli>
                </Styledul>
            ) : null}
            {input.length > 0 && noResults && !predicting ? (
                <Styledul>
                    <StyledNotFoundli onClick={handleNotFound}>
                        No result found. Add it manually
                    </StyledNotFoundli>
                </Styledul>
            ) : null}
            <StyledSearchContainer $isMobile={isSmallDevice}>
                <input
                    type="text"
                    value={input}
                    className="form-control search-address-input"
                    onChange={handleInputChange}
                    placeholder="Search for address..."
                />
                <Icon iconName="search-black.svg" />
            </StyledSearchContainer>
            {showAddress ? (
                <DefaultDeliveryAddress
                    displayOnly={displayOnly}
                    loading={loader}
                    isNameVisible={isNameVisible}
                />
            ) : null}
        </div>
    );
};

const Styledli = styled.li`
    list-style-type: none;
    display: flex;
    padding: 9px;
    margin-left: 0px;
    :hover {
        background: #ced4da;
        cursor: pointer;
    }
`;

const StyledNotFoundli = styled.li`
    list-style-type: none;
    padding: 9px;
    margin-left: 0px;
    cursor: pointer;
    text-align: center;
    text-decoration: underline;
`;

const Styledul = styled.ul`
    padding: 0px;
    border: 1px solid #ced4da;
    position: absolute;
    padding-top: 3px;
    top: 35px;
    left: 0;
    right: 0;
    background: white;
    z-index: 1;
`;

const StyledSearchContainer = styled.div<{$isMobile?: boolean}>`
    position: relative;
    ${({$isMobile}) => {
        if ($isMobile) return '';
        return 'margin-right: 15px;';
    }}
    img {
        width: 25px;
        position: absolute;
        right: 5px;
        top: 6px;
    }

    > input {
        padding-right: 35px;
    }
`;
